import { useEffect, useRef, useState } from 'react';

import { UseWatchApplicationStatusParams } from '../types/UseWatchApplicationStatusParams';

const DEFAULT_POLL_INTERVAL_MS = 20_000;

export const useWatchApplicationStatus = ({
  enabled,
  pollInterval = DEFAULT_POLL_INTERVAL_MS,
  statusToLeave,
  fetchLatestApplication,
}: UseWatchApplicationStatusParams): boolean => {
  const [hasLeftStatus, setHasLeftStatus] = useState(false);

  const fetchLatestApplicationRef = useRef(fetchLatestApplication);

  useEffect((): void => {
    fetchLatestApplicationRef.current = fetchLatestApplication;
  }, [fetchLatestApplication]);

  useEffect((): void => {
    if (enabled) {
      setHasLeftStatus(false);
    }
  }, [enabled, statusToLeave]);

  useEffect((): (() => void) => {
    if (!enabled || hasLeftStatus) {
      return;
    }

    let cancelled: boolean = false;
    let timer: number | null = null;

    const schedule = (): void => {
      timer = window.setTimeout(tick, pollInterval);
    };

    async function tick(): Promise<void> {
      if (cancelled) {
        return;
      }

      const application = await fetchLatestApplicationRef.current();

      if (application && application.status !== statusToLeave) {
        setHasLeftStatus(true);

        return;
      }

      schedule();
    }

    tick();

    return (): void => {
      cancelled = true;

      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [enabled, pollInterval, statusToLeave, hasLeftStatus]);

  return hasLeftStatus;
};
